import * as React from 'react';
import { cn } from './utils';
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { Button } from '@/ui/button';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

/** Figma frame 119:79's three widths, plus the two-column settings width
 *  frame 67:847 draws; a caller that names none keeps the pre-existing fixed
 *  width. */
const dialogSizeClassName = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-[720px]',
  xl: 'sm:max-w-[880px]',
} as const;

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size,
  scrim = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  size?: keyof typeof dialogSizeClassName;
  /** Radix's own `Overlay` renders nothing once the root is `modal={false}`
   *  (it reads that from context itself) -- a non-modal dialog that still
   *  wants the frame's dimming passes this instead: a plain div with none of
   *  `Overlay`'s Radix wiring, so it neither traps focus nor answers
   *  `onPointerDownOutside` on its own (pair it with an explicit
   *  `onPointerDownOutside` on this component if a click on it should not
   *  dismiss). It carries no `z-index` of its own, so a `fixed` chrome
   *  element with an explicit one (the sidebar's own `z-10`) keeps painting
   *  over it -- this component does not know the shell's layout, the caller
   *  does. */
  scrim?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      {scrim && <div data-slot="dialog-scrim" aria-hidden className="fixed inset-0 bg-black/50" />}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed top-[50%] left-[50%] z-50 flex max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col gap-lg overflow-y-auto rounded-xl border bg-card p-xl shadow-3 duration-slow outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          size ? dialogSizeClassName[size] : 'sm:max-w-lg',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-[var(--space-lg)] right-[var(--space-lg)] flex size-7 items-center justify-center rounded-md opacity-70 ring-offset-background transition-opacity hover:bg-secondary hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

// Figma frame 119:79's header leaves enough clear width for the close button
// (`size-7` at `right-[var(--space-lg)]`, absolute over `DialogContent`'s own
// `p-xl`) that no title ever runs under it.
function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-[var(--space-2xs)] pr-8 text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        // `-mx-5 -mb-5` cancel `DialogContent`'s own `p-xl` (20px == Tailwind's
        // native `5`) on 3 sides so the footer's own bg reaches the card's
        // rounded edges; the token can't be negated as an arbitrary value, so
        // this stays in sync with `--space-xl` by hand.
        'flex flex-col-reverse gap-[var(--space-sm)] sm:flex-row sm:justify-end',
        '-mx-5 -mb-5 rounded-b-xl border-t bg-secondary px-xl py-lg',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-title-md', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-body-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
